"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { ArrowRight, Loader, MapPin } from "lucide-react";
function CollaborationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-start justify-center h-full space-y-6 py-12 animate-in fade-in duration-500">
        <div className="size-12 rounded-md bg-primary/10 flex items-center justify-center text-primary">
          <ArrowRight className="size-6 -rotate-45" />
        </div>
        <h3 className="text-2xl font-default text-foreground uppercase tracking-tight">Solicitud Recibida</h3>
        <p className="text-muted-foreground font-default">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-border hover:bg-accent mt-4 font-default uppercase tracking-widest text-xs">
          Enviar otra consulta
        </Button>
      </div>;
  }
  const inputStyles = "border-0 border-b border-border rounded-md shadow-md px-0 py-3 bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors text-foreground font-default md:text-base h-auto";
  const labelStyles = "text-[10px] sm:text-xs uppercase tracking-widest text-muted-foreground font-default mb-1";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Ej. Juan Pérez" className={inputStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70 font-default">
                Indique su nombre para una atención personalizada.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El email es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ingrese un email válido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Correo Electrónico</FormLabel>
              <FormControl>
                <Input type="email" placeholder="juan@empresa.com" className={inputStyles} {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/70 font-default">
                Le responderemos a esta dirección a la brevedad.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-()]{7,15}$/,
          message: "Ingrese un número válido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Teléfono / WhatsApp</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+34 600 000 000" className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="brand_name" rules={{
        required: "La marca es obligatoria"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={labelStyles}>Marca de Maquinaria</FormLabel>
              <FormControl>
                <Input placeholder="HITACHI, CASE, DOOSAN..." className={inputStyles} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_type" rules={{
      required: "Seleccione el motivo"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelStyles}>Motivo de Consulta</FormLabel>
            <FormControl>
              <div className="relative">
                <select className={`w-full appearance-none outline-none ${inputStyles} cursor-pointer font-default`} {...field}>
                  <option value="" disabled>
                    Seleccione una opción...
                  </option>
                  {["Presupuesto de Recambios", "Consulta Técnica", "Otros"].map((option, i) => <option key={option} value={option} data-index={i} className="text-neutral-900">
                      {option}
                    </option>)}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                  <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="project_description" rules={{
      required: "La descripción es obligatoria"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={labelStyles}>Detalles del Recambio</FormLabel>
            <FormControl>
              <Textarea placeholder="Indique modelo de máquina, número de serie o referencia de la pieza..." className={`${inputStyles} min-h-[120px] resize-y font-default`} {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70 font-default">
              Cuanta más información nos facilite, más rápido podremos localizar su pieza.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm font-default">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm uppercase tracking-widest font-default transition-all shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Enviando...
            </> : <>
              Solicitar Información
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function LetsBuildSomethingTogether() {
  return <section data-section-id="223842" id="hablemos-de-su-maquinaria" className="w-full bg-background text-foreground py-24 lg:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          
          <div className="lg:col-span-5 flex flex-col space-y-12">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-default font-bold tracking-tighter text-white uppercase">COMO PODEMOS <span className="text-primary">AYUDARLe?</span></h2>
              <p className="text-lg text-muted-foreground font-default max-w-md leading-relaxed">Contacte con nuestro equipo técnico para encontrar la pieza exacta que necesita.</p>
            </div>

            <div className="space-y-8 pt-8 border-t border-border">
              <div>
                <h4 className="text-xs uppercase tracking-widest text-primary font-default mb-3">HORARIO DE ATENCIÓN A CLIENTE:</h4>
                <p className="text-xl font-default text-foreground uppercase">DE LUNES A VIERNES<br />De 8:30 a 17:00 HORAS</p>
              </div>

              <div className="flex flex-row gap-4">
                <div className="relative w-1/2 bg-muted/30 overflow-hidden group rounded-md shadow-md">
                  <img src="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/asistencia-telefonica.jpg" data-wvc-srcset="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/asistencia-telefonica.jpg-wvc-srcset" data-wvc-sizes="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/asistencia-telefonica.jpg-wvc-sizes" alt="Esquema técnico de maquinaria" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 border border-border/50 pointer-events-none" />
                </div>
                <div className="relative w-1/2 bg-muted/30 overflow-hidden group rounded-md shadow-md">
                  <img src="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/operari-al-telefono.jpg" data-wvc-srcset="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/operari-al-telefono.jpg-wvc-srcset" data-wvc-sizes="https://witty-mastiff.10web.cloud/wp-content/uploads/2026/05/operari-al-telefono.jpg-wvc-sizes" alt="Almacén de recambios industriales" className="w-full h-full object-cover object-right transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 border border-border/50 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8 xl:pl-16">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="LetsBuildSomethingTogether" defaultValues={{
            name: "",
            email: "",
            phone: "",
            brand_name: "",
            project_type: "Presupuesto de Recambios",
            project_description: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            brand_name: {
              required: true
            },
            project_type: {
              required: true
            },
            project_description: {
              required: true
            }
          }} submitText="Solicitar Información" successMessage="Su consulta ha sido enviada con éxito. Un especialista técnico se pondrá en contacto con usted en breve." className="w-full">
              <CollaborationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}